import { useLocation, useNavigate } from "react-router-dom";

import { Tabs, type OverflowItem } from "@/components/ui/Tabs";
import { stripQuery } from "./nav-config";
import { useActiveModule } from "./useActiveModule";

/** How many of a module's destinations render as real tabs before the rest
 * fall into "More". nav-config deliberately orders each module's admin-tier
 * items ahead of its personal/self-service and settings items, so taking
 * the leading items yields a sensible primary set without a second ordering
 * mechanism to maintain. */
const MAX_TABS = 5;

/** Routes whose page is one full-screen canvas: module strip without its description. */
const CANVAS_ROUTES = new Set(["/organization/chart"]);

/**
 * The context strip above every module page: the module's own name and
 * description, then its destinations as tabs with a "More" overflow.
 *
 * The sidebar stays stable (every module, always) - this is what changes as
 * you move between modules, replacing the old panel-swap where the sidebar
 * itself became module-specific and needed a "Back" to get out of.
 *
 * Every tab is an existing route, filtered through the same
 * `isNavItemVisible` gate the sidebar uses (via `useActiveModule`), so a
 * caller never sees a tab that would 403 the moment they clicked it. The
 * module name is an <h2>; each page keeps rendering its own PageHeader
 * <h1> underneath, so the two read as module -> page rather than competing
 * for the same level.
 *
 * Renders nothing when the current route belongs to no module (the
 * dashboard, /modules, /settings and the standalone account pages).
 */
export function ModuleHeader() {
  const module = useActiveModule();
  const location = useLocation();
  const navigate = useNavigate();

  if (!module || module.items.length === 0) return null;
  // A full-screen workspace (the org chart) keeps the module name and tabs -
  // they are how you get back to the rest of the module - but drops the
  // description line so the canvas gets that room.
  const compact = CANVAS_ROUTES.has(location.pathname);

  // Pages folded into a hub (NavItem.tabOf) are not tabs of their own while
  // that hub is visible to the caller; they light up the hub's tab instead.
  const hubTos = new Set(module.items.filter((item) => !item.tabOf).map((item) => item.to));
  const isFolded = (item: (typeof module.items)[number]) => Boolean(item.tabOf && hubTos.has(item.tabOf));
  const tabItems = module.items.filter((item) => !isFolded(item));

  // A module with exactly one destination has nothing to navigate between -
  // the page's own PageHeader already says where you are.
  if (tabItems.length <= 1) return null;

  // The longest matching path wins, so a nested route (/documents/hr)
  // highlights its own tab rather than a shorter sibling (/documents); on a
  // tie the item whose query string matches too (/leaves?action=apply) wins.
  const score = (to: string) => {
    const path = stripQuery(to);
    if (location.pathname !== path && !location.pathname.startsWith(path + "/")) return -1;
    const query = to.slice(path.length);
    return path.length * 2 + (query && location.search === query ? 1 : 0);
  };
  const matched = module.items
    .map((item) => ({ item, score: score(item.to) }))
    .filter((m) => m.score >= 0)
    .sort((a, b) => b.score - a.score)[0]?.item;
  const activeKey = matched ? (isFolded(matched) ? matched.tabOf! : matched.to) : "";

  // A "More" menu holding a single destination costs a click and hides the
  // page for nothing (Recruitment's Reports was the lone item), so one item
  // past the budget is shown as a tab instead; More appears from two.
  const budget = tabItems.length === MAX_TABS + 1 ? MAX_TABS + 1 : MAX_TABS;
  const primary = tabItems.slice(0, budget);
  const rest = tabItems.slice(budget);

  // An active destination that lives in the overflow is promoted into the
  // tab row - otherwise the current page would have no visible tab and the
  // indicator would vanish while you are standing on it.
  const activeInRest = rest.find((item) => item.to === activeKey);
  const tabs = (activeInRest ? [...primary, activeInRest] : primary).map((item) => ({
    key: item.to,
    label: item.label,
  }));
  const overflow: OverflowItem[] = rest
    .filter((item) => item.to !== activeKey)
    .map((item) => ({ key: item.to, label: item.label, group: item.group }));

  return (
    <div className={compact ? "mb-3" : "mb-5"}>
      <h2 className="font-display text-lg font-semibold tracking-tight text-slate-900">{module.label}</h2>
      {module.description && !compact && <p className="mb-3 mt-0.5 text-sm text-slate-500">{module.description}</p>}
      <Tabs tabs={tabs} active={activeKey} onChange={(to) => navigate(to)} overflow={overflow} />
    </div>
  );
}
